import {inject, Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {IServerApi, MileageDataSource, VehicleType} from '@service-and-repairs/awpintegrationlib';
import {Observable, ReplaySubject} from 'rxjs';
import {AwpClientLibService} from '../../../services/awp-client-lib.service';
import {MileageRecord} from '../../mileage/interfaces/mileage-record';
import {UserData} from '../../user/models/user-data';
import {UserService} from '../../user/services/user.service';
import {KeyData} from '../interfaces/key-data';
import {KeyDataService} from './key-data.service';

@Injectable({
  providedIn: 'root'
})
export class KeyDataLoader {
  private readonly keyDataService: KeyDataService = inject(KeyDataService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly userService: UserService = inject(UserService);
  private readonly awpClientLibService: AwpClientLibService = inject(AwpClientLibService);

  awpLib: IServerApi;

  private keyData: KeyData[];
  keyDataChanged = new Observable<KeyData[]>();
  private readonly keyDataChangedSubject = new ReplaySubject<KeyData[]>(1);

  private distributionPartnerNumber: string;
  private outletNumber: string;

  constructor() {
    const userService = this.userService;
    this.awpLib = this.awpClientLibService.serverApi;
    this.keyDataChanged = this.keyDataChangedSubject.asObservable();
    userService.user$.subscribe(this.updateKeyPoolIfOutletHasChanged.bind(this));
  }

  getKeyDataForVin(vin: string): KeyData | undefined {
    return this.keyData?.find(kd => kd.vin === vin);
  }

  getMileageForVin(vin: string, vehicleType: VehicleType): MileageRecord {
    const keyData = this.keyData?.find(kd => kd.vin === vin);

    return keyData
      ? {
        value: keyData.totalDistanceInKm,
        source: keyData.keyOrigin === 'VIN_KEYCARD' ? MileageDataSource.OVER_THE_AIR : MileageDataSource.KEY_READER,
        vin: vin,
        recordedAt: vehicleType === VehicleType.PASSENGER_CAR ? keyData.writeDate : keyData.readInDate,
        createdAt: keyData.writeDate,
        lastUpdatedAt: keyData.readInDate,
        errors: []
      }
      : undefined;
  }

  reloadKeyData(): void {
    const user = this.userService.getCurrentUser();
    this.keyDataService.getKeyData(user.getBusinessPartner())
      .then(keyData => {
        this.keyData = keyData;
        this.keyDataChangedSubject.next(keyData);
      })
      .catch(reason => {
        this.keyDataChangedSubject.error(reason);
        this.showErrorMessageOnceTranslationExists('common.keyPoolError');
      });
  }

  private updateKeyPoolIfOutletHasChanged(user: UserData): void {
    if (user?.getBusinessPartner() && this.hasOutletChanged(user)) {
      this.distributionPartnerNumber = user.getBusinessPartner().getDistributionPartnerNumber();
      this.outletNumber = user.getBusinessPartner().getOutletNumber();

      this.reloadKeyData();
    }
  }

  private hasOutletChanged(user: UserData): boolean {
    const newDistributionPartnerNumber = user?.getBusinessPartner()?.getDistributionPartnerNumber();
    const newOutletNumber = user?.getBusinessPartner()?.getOutletNumber();

    return newDistributionPartnerNumber !== this.distributionPartnerNumber ||
      newOutletNumber !== this.outletNumber;
  }

  private showErrorMessageOnceTranslationExists(key: string): void {
    this.translate.get(key).subscribe((translation: string) => {
      this.awpLib.notifyError(translation);
    });
  }
}
